import {
  useEffect,
  useState,
  useRef,
  ChangeEvent,
  MouseEventHandler,
} from "react";
import { RootState } from "..//store/store";
import { useSelector, useDispatch } from "react-redux";
import { setPlaces, setCalendars } from "..//store/features/cacheSlice";
import toast, { Toaster } from "react-hot-toast";
import ContentLayout from "..//components/ContentLayout";
import Datagrid from "..//components/Datagrid/Datagrid";
import CalendarBackend from "..//components/Calendar/utils/CalendarBackend";
import CreateEditDeleteButtonGroup from "..//components/CreateEditDeleteButtonGroup";
import { BackendApi } from "../utils/BackendApi";
import { SelectionChangedEvent, ColDef } from "ag-grid-community";
import "bootstrap/dist/css/bootstrap.min.css";
import common from "..//utils/common";
import { ApiRoute } from "..//utils/ApiRoute";
import { useWindowSize } from "../hooks/useWindowSize";
import { AxiosResponse } from "axios";
import moment from "moment";
import RecordSearchInput from "..//components/Datagrid/RecordSearchInput";
import { cal_calendar, cal_place, cal_record } from "../class/prisma/db";
import { AgGridReact } from "ag-grid-react";
import EventModal from "../components/Modal/EventModal";
import { SystemMsg } from "../utils/SystemMsg";
import { ColorColumnCell } from "../components/ColorColumnCell";
import "bootstrap/dist/css/bootstrap.css";
import "./page.css";
import { CalendarViewObject } from "../class/calendar.schema";

import EventMultiModal from "../components/Modal/EventMultiModal";
import {
  UpdateRecordMultiRequest,
  SearchRecordRequest,
} from "../class/record.schema";
import { useI18n } from "../i18n";

const Title = () => {
  const { t } = useI18n();
  const title = t("nav.records") + " - " + t("meta.appName");
  return (
    <>
      <title>{title}</title>
    </>
  );
};
const Content = () => {
  const dispatch = useDispatch();
  const gridRef = useRef<AgGridReact>(null);

  const places = useSelector((state: RootState) => state.cache.places);
  const calendars = useSelector((state: RootState) => state.cache.calendars);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [crudMode, setCrudMode] = useState("view");
  const [showSingleModal, setShowSingleModal] = useState(false);
  const [showMultiModal, setShowMultiModal] = useState(false);

  const [modalDataObj, setModalDataObj] = useState<
    cal_record | cal_record[] | undefined
  >(undefined);

  const [rowData, setRowData] = useState<any[]>();
  const [rowLoaded, setRowLoaded] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(common?.pageSizeArray[1]);
  const [pageCount, setPageCount] = useState(0);
  const [paginationPage, setPaginationPage] = useState(0);

  //Search
  const [keyword, setKeyword] = useState("");
  const [searchStartDate, setSearchStartDate] = useState("");
  const [searchEndDate, setSearchEndDate] = useState("");

  const { height } = useWindowSize();
  const colDefs = [
    { field: "id", width: "70" },

    {
      field: "calendarName",
      headerName: "Calendar",
      width: "150",
      cellRenderer: (params: any) => {
        return calendarNameRenderer(params);
      },
    },
    { field: "name", width: "200" },
    {
      field: "description",
      wrapText: true,
      autoHeight: true,
      cellRenderer: (params: any) => {
        return descriptionRenderer(params);
      },
      flex: 1,
    },
    {
      field: "start_date",
      headerName: "Start",
      width: "200",
      cellRenderer: (params: any) => {
        return dateRenderer(params);
      },
    },
    {
      field: "end_date",
      headerName: "End",
      width: "200",
      cellRenderer: (params: any) => {
        return dateRenderer(params);
      },
    },

    {
      field: "place_id",
      headerName: "Place",
      width: "150",
      cellRenderer: (params: any) => {
        return placeIdRenderer(params);
      },
    },
  ] as ColDef[];

  useEffect(() => {
    if (!Array.isArray(places)) {
      CalendarBackend.getPlaceData((data) => {
        if (Array.isArray(data)) {
          dispatch(setPlaces(data));
        }
      });
    }
  }, [places]);

  useEffect(() => {
    if (!Array.isArray(calendars)) {
      CalendarBackend.getCalendarData((data) => {
        if (Array.isArray(data)) {
          console.log(`calendars`, data);
          dispatch(setCalendars(data));
        }
      });
    }
  }, [calendars]);

  const descriptionRenderer = (param: any) => {
    if (!param || !param.value) {
      return param.value;
    }
    const lineArray = param.value
      .toString()
      .split("\n")
      .filter((x: any) => x.trim().length > 0);
    const singleLine = lineArray.length === 1;
    if (lineArray.length === 0) {
      return param.value;
    }

    return (
      <p
        style={{
          lineHeight: !singleLine ? 1.8 : "unset",
          cursor: "pointer",
          margin: 0,
        }}
        onClick={(e) => descriptionClick(e)}
      >
        {lineArray.map((str: any, idx: any) => (
          <span key={idx}>
            {str}
            {!singleLine && idx !== lineArray.length - 1 && (
              <>
                {`\n`}
                <br />
              </>
            )}
          </span>
        ))}
      </p>
    );
  };

  const descriptionClick = (elem: React.MouseEvent<HTMLParagraphElement>) => {
    const isParagraph = elem.target instanceof HTMLParagraphElement;
    const para = isParagraph
      ? (elem.target as HTMLParagraphElement)
      : ((elem.target as any).parentNode as HTMLParagraphElement);

    navigator.clipboard.writeText(para.textContent ?? "").then(
      () => {
        toast.success(SystemMsg.GridUi.copiedToClipboard);
      },
      (err) => {
        console.error("Could not copy text: ", err);
      },
    );
  };

  const calendarNameRenderer = (param: any) => {
    if (!param || !param.value) {
      return param.value;
    }
    const calendarColor = param.data.calendarColor;
    return <ColorColumnCell color={calendarColor} text={param.value} />;
  };

  const getCalendarObject = (id: number): cal_calendar | undefined => {
    if (!Array.isArray(calendars) || (calendars as any[]).length === 0) {
      return;
    }
    const list = calendars as cal_calendar[];
    const find = list.find((x) => x.id === id);
    return find;
  };

  const placeIdRenderer = (param: any) => {
    if (!places) {
      return param.value;
    }
    const list = places as any[];
    if (!Array.isArray(list) || list.length === 0) {
      return param.value;
    }
    if (!param || !param.value) {
      return param.value;
    }
    if (!param.value) {
      return param.value;
    }
    const find = list.find((x) => x.id === param.value);
    if (!find) {
      return param.value;
    }
    return find.name;
  };

  const dateRenderer = (param: any) => {
    if (!param || !param.value) {
      return "";
    }
    if (!param.value) {
      return "";
    }

    const dateObj = moment(param.value);
    if (!dateObj.isValid()) {
      return "Invalid Date";
    }
    return dateObj.format(`YYYY-MM-DD HH:mm`);
  };

  const getSearchParam = () => {
    const searchParam: SearchRecordRequest = {
      page: page > 0 ? page : 1,
      pageSize: pageSize,
      orderBy: JSON.stringify({ start_date: "desc" }),
    };
    if (
      !common.stringIsNullOrEmpty(keyword) &&
      keyword.toString().trim().length > 0
    ) {
      searchParam[`keyword`] = keyword;
    }
    if (searchStartDate.length >= 6 && moment(searchStartDate).isValid()) {
      searchParam[`start_date`] = moment(searchStartDate).toDate();
    }
    if (searchEndDate.length >= 6 && moment(searchEndDate).isValid()) {
      searchParam[`end_date`] = moment(searchEndDate).toDate();
    }
    return searchParam;
  };
  const getRowData = () => {
    //common.setDataGridLoading(gridRef, true);
    BackendApi.get(
      ApiRoute.records,
      getSearchParam(),
      (resp) => {
        //common.setDataGridLoading(gridRef, false);
        const total =
          resp.data.total != null && !Number.isNaN(resp.data.total.toString())
            ? resp.data.total
            : 0;
        setPageCount(common.getPageCount(total, pageSize));
        if (Array.isArray(resp.data.data)) {
          setRowData(transformRowData(resp.data.data));
        } else {
          setRowData([]);
        }
        setRowLoaded(true);
      },
      (resp) => {
        // common.setDataGridLoading(gridRef, false);
        toast.error(SystemMsg.Record.failedToLoadRecord);
        console.log(resp);
        setRowLoaded(true);
      },
    );
  };

  const transformRowData = (data: any[]) => {
    const dataArray: any[] = [];
    if (!Array.isArray(data)) {
      return dataArray;
    }

    for (const item of data) {
      const calendar = getCalendarObject(item.calendar_id);
      const calendarName = calendar?.name ?? "";
      const calendarColor = calendar?.color ?? "";
      dataArray.push({
        ...item,
        calendarName: calendarName,
        calendarColor: calendarColor,
      });
    }

    return dataArray;
  };

  const onSelectionChanged = (event: SelectionChangedEvent) => {
    if (
      !event ||
      !event.selectedNodes ||
      event.selectedNodes === null ||
      !Array.isArray(event.selectedNodes) ||
      event.selectedNodes.length === 0
    ) {
      setSelectedIds([]);
      return;
    }
    const selectedNode = event.selectedNodes;
    setSelectedIds(
      selectedNode
        .filter((x) => Number.isInteger(Number(x.data.id)))
        .map((x) => Number(x.data.id)),
    );
  };

  const onModalClose = () => {
    setCrudMode("");

    if (showSingleModal) {
      setShowSingleModal(false);
    }

    if (showMultiModal) {
      setShowMultiModal(false);
    }

    setModalDataObj(undefined);
    setSelectedIds([]);
    gridRef?.current?.api?.deselectAll();
  };

  const deleteRecord = (dataObj: any, callbackFunc: (record: any) => void) => {
    const id = Number(dataObj?.id);
    if (!Number.isInteger(id) || id <= 0) {
      toast.error(SystemMsg.Record.failedToFindRecordId);
      return;
    }
    CalendarBackend.deleteRecord(
      id,
      (resp: AxiosResponse) => {
        const success = resp.status >= 200;
        if (success) {
          toast.success(SystemMsg.Record.deleteSuccess(id));
          if (typeof callbackFunc === "function") {
            callbackFunc(dataObj);
          }
        }
      },
      (resp: AxiosResponse | string) => {
        const response = resp as AxiosResponse;
        const errMessage = response?.data?.message ?? "";
        if (errMessage.length === 0) {
          toast.error(SystemMsg.Record.deleteFailed(id));
          return;
        }
        toast.error(errMessage);
      },
    );
  };

  const onCreateButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setCrudMode("add");
    setShowSingleModal(true);
    setModalDataObj(undefined);
  };

  const onEditButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!rowData) {
      toast.error(SystemMsg.GridUi.emptyData);
      return;
    }

    if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
      toast.error(SystemMsg.GridUi.emptySelection);
      return;
    }

    const singleSelect = selectedIds.length === 1;

    setCrudMode("edit");

    if (singleSelect) {
      const find = rowData?.find((x) => x.id === selectedIds[0]);
      if (!find || find.length === 0) {
        toast.error(SystemMsg.GridUi.emptySelection);
        return;
      }
      setModalDataObj(find);
      setShowSingleModal(true);
    } else {
      const filter = rowData?.filter((x) => selectedIds.includes(Number(x.id)));
      if (!filter || filter.length === 0) {
        toast.error(SystemMsg.GridUi.emptySelection);
        return;
      }
      setModalDataObj(filter);
      setShowMultiModal(true);
    }
  };

  const onDeleteButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const singleSelect = selectedIds.length === 1;
    if (singleSelect) {
      const find = rowData?.find((x) => x.id === selectedIds[0]);
      if (!find) {
        toast.error(SystemMsg.GridUi.emptySelection);
        return;
      }
      deleteRecord(find, (record: any) => {
        gridRef?.current?.api.applyTransaction({ remove: [record] });
        const findIdx = rowData?.indexOf(find);
        if (findIdx === undefined || findIdx < 0) {
          return;
        }
        rowData?.splice(findIdx, 1);
      });
      return;
    }
    const filter = rowData?.filter((x) => selectedIds.includes(Number(x.id)));
    if (!filter || filter.length === 0) {
      toast.error(SystemMsg.GridUi.emptySelection);
      return;
    }
    setCrudMode("delete");
    setModalDataObj(filter);
    setShowMultiModal(true);
  };

  const topMenuElement = (
    <CreateEditDeleteButtonGroup
      onCreateButtonClick={onCreateButtonClick}
      onEditButtonClick={onEditButtonClick}
      onDeleteButtonClick={onDeleteButtonClick}
      hasEdit={true}
      disableEdit={selectedIds.length === 0}
      hasDelete={selectedIds.length > 0}
    />
  );

  useEffect(() => {
    if (Array.isArray(calendars)) {
      getRowData();
    }
  }, [calendars]);

  useEffect(() => {
    setPage(1);
    setPaginationPage(0);
  }, [pageSize]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (rowLoaded) {
        getRowData();
      }
    }, common.DEBOUNCE_TIME);
    return () => clearTimeout(debounce);
  }, [keyword, searchStartDate, searchEndDate]);

  useEffect(() => {
    if (rowLoaded) {
      getRowData();
    }
  }, [page, pageSize]);

  const handlePageClick = (event: { selected: number }) => {
    if (!event || event?.selected == null || Number.isNaN(event.selected)) {
      return;
    }
    setPage(event.selected + 1);
    setPaginationPage(event.selected);
  };

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (
      !event ||
      !event?.target ||
      event?.target?.value == null ||
      Number.isInteger(event?.target?.value)
    ) {
      return;
    }
    setPageSize(Number(event.target.value));
  };

  const topLeftElement = (
    <RecordSearchInput
      searchValue={keyword}
      setSearchValueFunc={setKeyword}
      startDate={searchStartDate}
      setStartDateFunc={setSearchStartDate}
      endDate={searchEndDate}
      setEndDateFunc={setSearchEndDate}
    />
  );

  const onSaveBtnClick = (
    resp: AxiosResponse,
    api: any,
    mode?: string,
    data?: any,
  ) => {
    const success = resp.status === 200;
    const isAdd = mode === "add";
    if (!success) {
      toast.error(
        isAdd
          ? SystemMsg.Record.createFailed
          : SystemMsg.Record.updateFailed(data?.id ?? -1),
      );
      return;
    }

    if (isAdd) {
      const data = resp.data as cal_record[];
      const singleAdd = data.length > 1;
      const num = singleAdd ? data[0].id : data.length;
      toast.success(
        singleAdd
          ? SystemMsg.Record.createOneSuccess(num)
          : SystemMsg.Record.createManySuccess(num),
      );
      getRowData();
    } else {
      if (!rowData) {
        toast.error(SystemMsg.GridUi.emptyData);
        return;
      }
      const obj = data as cal_record;
      if (obj.id && obj.id.toString().length > 0) {
        obj.id = Number(obj.id);
      }
      const find = rowData?.find((x) => x.id.toString() === obj.id.toString());
      const findIdx = rowData?.indexOf(find);
      if (!find || findIdx === undefined || findIdx < 0) {
        toast.error(SystemMsg.GridUi.doesNotIncludeRecord(obj.id));
        return;
      }
      const tempArray = transformRowData([obj]);
      rowData[findIdx] = tempArray[0];
      const rowNode = gridRef?.current?.api?.getRowNode(findIdx.toString());
      rowNode?.setData(tempArray[0]);
    }
  };

  const onConfirmClick = (
    resp: AxiosResponse,
    mode?: string,
    data?: UpdateRecordMultiRequest,
  ) => {
    const success = resp.status === 200;
    const isEdit = mode === "edit";
    const isDelete = mode === "delete";
    if (!success) {
      toast.error(
        isEdit
          ? SystemMsg.Record.updateMultiFailed(data?.ids ?? [])
          : SystemMsg.Record.deleteMultiFailed(data?.ids ?? []),
      );
      return;
    }

    if (!rowData) {
      toast.error(SystemMsg.GridUi.emptyData);
      return;
    }

    const filter = rowData?.filter((x) =>
      data?.ids.includes(Number(x.id.toString())),
    );
    if (!filter || filter === undefined || filter.length <= 0) {
      toast.error(
        SystemMsg.GridUi.doesNotIncludeMultiRecord(data?.ids as number[]),
      );
      return;
    }

    for (const entry of filter) {
      const findIdx = rowData.indexOf(entry);
      if (isEdit) {
        const newData: cal_record = {
          ...entry,
        };
        if (data?.calendar_id) {
          newData[`calendar_id`] = data?.calendar_id;
        }
        if (data?.place_id) {
          newData[`place_id`] = data?.place_id;
        }

        const tempArray = transformRowData([newData]);
        rowData[findIdx] = tempArray[0];
        const rowNode = gridRef?.current?.api?.getRowNode(findIdx.toString());
        rowNode?.setData(tempArray[0]);
      }
      if (isDelete) {
        gridRef?.current?.api.applyTransaction({ remove: [entry] });
      }
    }

    if (isDelete) {
      const newRowData = rowData.filter(
        (x) => !filter.find((y) => y.id === x.id),
      );
      setRowData([...newRowData]);
    }
  };

  const getPageSizeArray = () => {
    const temp = [...common.pageSizeArray];
    temp.shift();
    return temp;
  };

  return (
    <>
      <Datagrid
        rowSelect="multiRow"
        gridRef={gridRef}
        rowData={rowData}
        colDefs={colDefs}
        onSelectionChanged={onSelectionChanged}
        topLeftElement={topLeftElement}
        topRightElement={topMenuElement}
        hasControls={true}
        hasPagination
        handlePageClick={handlePageClick}
        handlePageSizeChange={handlePageSizeChange}
        paginatePage={paginationPage}
        paginatePageCount={pageCount}
        paginatePageSizeArray={getPageSizeArray()}
        windowHeight={height}
        loading={!rowLoaded}
      />
      <EventModal
        toast={toast}
        mode={crudMode}
        show={showSingleModal}
        data={modalDataObj}
        onSaveBtnClick={onSaveBtnClick}
        onClose={onModalClose}
        calendars={(calendars as unknown as CalendarViewObject[]) ?? undefined}
        places={(places as unknown as cal_place[]) ?? undefined}
        isCalendar={false}
      />
      <EventMultiModal
        mode={crudMode}
        toast={toast}
        show={showMultiModal}
        data={modalDataObj}
        calendars={(calendars as unknown as CalendarViewObject[]) ?? undefined}
        places={(places as unknown as cal_place[]) ?? undefined}
        onClose={onModalClose}
        onConfirmClick={onConfirmClick}
      />
      <Toaster
        position="top-center"
        reverseOrder={false}
        toastOptions={{
          className: "",
          style: common.toasterStyle,
        }}
      />
    </>
  );
};
const Record = () => {
  return (
    <ContentLayout
      isFluid={true}
      maxWidth="80vw"
      header={<Title />}
      content={<Content />}
    />
  );
};
export default Record;
